/**
 * @packageDocumentation
 * @module f1/abstract-data-type
 */

import { t_rec_arr, t_rec, t_key } from '../types';
import is_array from '../is/is_array';
import is_pojo from '../is/is_record';

/** @ignore */
export type t_stop_fn = () => void;

/** @ignore */
export type t_foldable_fn<V, A> = (acc: A, v: V, k: t_key, foldable: t_rec_arr<V>, stop: t_stop_fn) => A;

export interface Fold {
  /**
   * fold is used by adt methods and exposes itself as `f1.reduce`
   *
   * @public
   * @function f1.fold
   * @param fn
   * @param acc
   * @param adt
   * @see f1.reduce
   */
  <V, A, R>(fn: t_foldable_fn<V, A>, acc: A, adt: t_rec_arr<V>): R;
  <V, A, R>(fn: t_foldable_fn<V, A>, acc: A): (adt: t_rec_arr<V>) => R;
}

/**
 * @see Fold
 */
export default <V, A, R>(fn: t_foldable_fn<V, A>, acc: A, adt: t_rec_arr<V>): R => {
  const isArray = is_array(adt);
  const isPojo = is_pojo(adt);
  const list = isArray ? adt : Object.keys(adt);
  const n = list.length;
  let key: string;
  let i = 0;

  let stopNow = false;
  const stop = () => (stopNow = true);

  if (isArray) {
    for (; i < n; i++) {
      acc = fn(acc, list[i] as V, i as t_key, adt, stop);
      if (stopNow) {
        break;
      }
    }
  } else if (isPojo) {
    for (; i < n; i++) {
      key = list[i] as string;
      acc = fn(acc, (adt as t_rec<t_key, V>)[key] as V, key, adt, stop);
      if (stopNow) {
        break;
      }
    }
  } else {
    acc = fn(acc, (adt as unknown) as V, '', adt, stop);
  }

  return (acc as unknown) as R;
};
